import { Signal, WritableSignal } from "@angular/core";

/** Minimal location shape the shared queue footer renders. */
export interface IQueueLocation {
  id: string;
  name: string;
}

/**
 * App/page-specific bindings for the shared queue layout (header + footer).
 *
 * The queue layout lives in shared-core and is reused across apps, but its
 * chrome needs data it can't derive itself:
 *  - `location`: shown in the footer — derived differently per app (the customer
 *    kiosk from its guest auth session, the staff app from queue preferences).
 *  - `fullscreenToggle`: an optional fullscreen control contributed by the
 *    active page (via the queue fullscreen guard). The header renders its
 *    fullscreen button only while a handler is registered; the guard sets it on
 *    activation and clears it on teardown. This is purely the header↔guard
 *    bridge — exit protection (PIN) is configured on the guard itself.
 *
 * Each app provides a concrete implementation via DI where the queue layout is
 * mounted, keeping the layout itself project-agnostic.
 */
export abstract class QueueLayoutAbstraction {
  /** Current location to display in the footer, or `null` when none is set. */
  abstract readonly location: Signal<IQueueLocation | null>;

  /** Fullscreen toggle contributed by the active page, or `null` when none. */
  abstract readonly fullscreenToggle: WritableSignal<(() => void) | null>;
}
